/* PUBLIC Snowboards dealer locator. Requires public-stores.js and Mapbox GL JS. */
(function () {
  'use strict';

  const instances = new WeakMap();
  const EARTH_RADIUS_MILES = 3958.7613;

  function makeElement(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  // Great-circle distance: coordinate arrays are [longitude, latitude].
  // Distances are approximate straight-line distances, not driving miles.
  function distanceMiles(from, to) {
    const toRadians = Math.PI / 180;
    const lat1 = from[1] * toRadians;
    const lat2 = to[1] * toRadians;
    const deltaLat = (to[1] - from[1]) * toRadians;
    const deltaLng = (to[0] - from[0]) * toRadians;
    const a = Math.sin(deltaLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function distanceLabel(miles) {
    return (miles < 10 ? miles.toFixed(1) : Math.round(miles).toLocaleString()) + ' mi away';
  }

  function init(root) {
    if (instances.has(root)) return;

    const mapElement = root.querySelector('.public-dealer-locator__map');
    const listingElement = root.querySelector('.public-dealer-locator__listings');
    const countElement = root.querySelector('.public-dealer-locator__count');
    const searchElement = root.querySelector('.public-dealer-locator__search');
    const locateButton = root.querySelector('.public-dealer-locator__locate');
    const locationMessage = root.querySelector('.public-dealer-locator__location-message');
    const token = root.dataset.mapboxToken;
    const features = window.PUBLIC_DEALER_STORES && window.PUBLIC_DEALER_STORES.features;

    if (!window.mapboxgl || !Array.isArray(features) || !token) {
      countElement.textContent = 'Dealer map unavailable. Check the Mapbox token and Shopify assets.';
      if (locateButton) locateButton.disabled = true;
      return;
    }

    const dealers = features.filter(function (feature) {
      const c = feature && feature.geometry && feature.geometry.coordinates;
      return feature && feature.properties && Array.isArray(c) && c.length === 2 &&
        Number.isFinite(c[0]) && Number.isFinite(c[1]) &&
        c[0] >= -180 && c[0] <= 180 && c[1] >= -90 && c[1] <= 90;
    });

    const map = new mapboxgl.Map({
      container: mapElement,
      accessToken: token,
      style: 'mapbox://styles/mapbox/standard',
      center: [-100, 42],
      zoom: 3,
      config: { basemap: { theme: 'faded' } }
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const pins = [];
    let visibleListings = new Map();
    let currentActiveIndex = -1;
    let visitorCoordinates = null;
    let visitorMarker = null;
    let mapReady = false;
    let locating = false;
    let destroyed = false;

    // The theme editor can unload and recreate this section without reloading the page.
    instances.set(root, {
      map: map,
      destroy: function () {
        destroyed = true;
        if (visitorMarker) visitorMarker.remove();
        map.remove();
      }
    });

    function getSortedMatches(query) {
      const term = (query || '').trim().toLocaleLowerCase();
      const result = [];
      dealers.forEach(function (store, index) {
        const p = store.properties;
        const fields = [p.name, p.address, p.city, p.state, p.postalCode, p.country];
        if (!term || fields.some(function (value) {
          return String(value || '').toLocaleLowerCase().includes(term);
        })) {
          result.push({
            index: index,
            miles: visitorCoordinates ? distanceMiles(visitorCoordinates, store.geometry.coordinates) : null
          });
        }
      });
      if (visitorCoordinates) {
        result.sort(function (a, b) {
          return a.miles - b.miles || a.index - b.index;
        });
      }
      return result;
    }

    function selectStore(index) {
      currentActiveIndex = index;
      pins.forEach(function (pin, i) {
        pin.element.classList.toggle('is-active', i === index);
      });
      visibleListings.forEach(function (listing, i) {
        listing.classList.toggle('is-active', i === index);
      });
      const listing = visibleListings.get(index);
      if (listing) {
        const desiredTop = listing.offsetTop - listingElement.offsetTop - 10;
        listingElement.scrollTo({ top: desiredTop, behavior: 'smooth' });
      }
      map.flyTo({ center: dealers[index].geometry.coordinates, zoom: 12, duration: 900 });
    }

    function buildListing(store, index, miles) {
      const p = store.properties;
      const item = makeElement('div', 'public-dealer-locator__store');
      item.setAttribute('role', 'listitem');
      item.tabIndex = 0;

      item.appendChild(makeElement('h3', 'public-dealer-locator__store-name', p.name || 'Dealer'));
      if (miles !== null) {
        item.appendChild(makeElement('p', 'public-dealer-locator__distance', distanceLabel(miles)));
      }

      const details = makeElement('div', 'public-dealer-locator__store-details');
      const addressParts = [p.address, p.city, p.state, p.postalCode, p.country].filter(Boolean);
      if (addressParts.length) details.appendChild(makeElement('p', '', addressParts.join(', ')));
      if (p.phoneFormatted || p.phone) {
        const phoneP = makeElement('p');
        const phone = makeElement('a', '', p.phoneFormatted || p.phone);
        const tel = String(p.phone || p.phoneFormatted).replace(/[^\d+]/g, '');
        phone.href = 'tel:' + tel;
        phone.addEventListener('click', function (event) { event.stopPropagation(); });
        phoneP.appendChild(phone);
        details.appendChild(phoneP);
      }
      if (p.url && /^https?:\/\//i.test(p.url)) {
        const websiteP = makeElement('p');
        const link = makeElement('a', '', 'Website');
        link.href = p.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.addEventListener('click', function (event) { event.stopPropagation(); });
        websiteP.appendChild(link);
        details.appendChild(websiteP);
      }
      item.appendChild(details);
      item.addEventListener('click', function () { selectStore(index); });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectStore(index);
        }
      });
      return item;
    }

    function render() {
      const term = searchElement.value.trim();
      const matches = getSortedMatches(term);
      const matchingIndexes = new Set(matches.map(function (entry) { return entry.index; }));
      listingElement.replaceChildren();
      visibleListings = new Map();

      matches.forEach(function (entry) {
        const item = buildListing(dealers[entry.index], entry.index, entry.miles);
        item.classList.toggle('is-active', entry.index === currentActiveIndex);
        visibleListings.set(entry.index, item);
        listingElement.appendChild(item);
      });
      pins.forEach(function (pin, index) {
        const shouldShow = matchingIndexes.has(index);
        if (shouldShow && !pin.visible) {
          pin.marker.addTo(map);
          pin.visible = true;
        } else if (!shouldShow && pin.visible) {
          pin.marker.remove();
          pin.visible = false;
        }
      });
      if (currentActiveIndex >= 0 && !matchingIndexes.has(currentActiveIndex)) {
        currentActiveIndex = -1;
        pins.forEach(function (pin) { pin.element.classList.remove('is-active'); });
      }
      if (!matches.length) {
        listingElement.appendChild(makeElement('p', 'public-dealer-locator__empty', 'No dealers match that search.'));
      }
      const count = term ? 'Showing ' + matches.length + ' of ' + dealers.length + ' dealers' : dealers.length + ' dealers';
      countElement.textContent = visitorCoordinates ? count + ' · Nearest first' : count;
      if (visitorCoordinates) listingElement.scrollTop = 0;
    }

    function focusNearby() {
      if (!visitorCoordinates || !mapReady) return;
      const closest = getSortedMatches('').slice(0, 4);
      const bounds = new mapboxgl.LngLatBounds(visitorCoordinates, visitorCoordinates);
      closest.forEach(function (entry) {
        bounds.extend(dealers[entry.index].geometry.coordinates);
      });
      if (closest.length) {
        map.fitBounds(bounds, { padding: 35, maxZoom: 10, duration: 800 });
      } else {
        map.flyTo({ center: visitorCoordinates, zoom: 9, duration: 800 });
      }
    }

    function locationErrorMessage(error) {
      if (error && error.code === 1) {
        return 'Location access was denied. Allow location in your browser settings, or search by shop or state.';
      }
      if (error && error.code === 3) {
        return 'Location timed out. Try again, or search by shop name or state.';
      }
      return 'Could not determine your location. You can still search by shop name or state.';
    }

    function useMyLocation() {
      if (locating || destroyed) return;
      if (!navigator.geolocation || !window.isSecureContext) {
        locationMessage.textContent = 'Location requires HTTPS and browser support. Search by city or ZIP code instead.';
        return;
      }
      locating = true;
      locateButton.disabled = true;
      locateButton.textContent = 'Finding your location…';
      locationMessage.textContent = '';

      navigator.geolocation.getCurrentPosition(function (position) {
        if (destroyed) return;
        locating = false;
        locateButton.disabled = false;
        locateButton.textContent = 'Update my location';
        const longitude = position.coords.longitude;
        const latitude = position.coords.latitude;
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          locationMessage.textContent = 'Invalid location returned. Try again or search by city.';
          return;
        }
        visitorCoordinates = [longitude, latitude];
        locationMessage.textContent = 'Showing nearest dealers to your location. Zoom out to see more.';
        if (!visitorMarker) {
          const element = makeElement('div', 'public-dealer-locator__you');
          element.setAttribute('aria-label', 'Your location');
          visitorMarker = new mapboxgl.Marker({ element: element, anchor: 'center' })
            .setLngLat(visitorCoordinates).addTo(map);
        } else {
          visitorMarker.setLngLat(visitorCoordinates);
        }
        currentActiveIndex = -1;
        pins.forEach(function (pin) { pin.element.classList.remove('is-active'); });
        render();
        focusNearby();
      }, function (error) {
        if (destroyed) return;
        locating = false;
        locateButton.disabled = false;
        locateButton.textContent = visitorCoordinates ? 'Update my location' : 'Use my location';
        locationMessage.textContent = locationErrorMessage(error);
      }, {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 600000
      });
    }

    dealers.forEach(function (store, index) {
      const element = makeElement('button', 'public-dealer-locator__marker');
      element.type = 'button';
      element.setAttribute('aria-label', 'View ' + (store.properties.name || 'dealer'));
      element.addEventListener('click', function (event) {
        event.stopPropagation();
        selectStore(index);
      });
      const marker = new mapboxgl.Marker({ element: element, anchor: 'bottom' })
        .setLngLat(store.geometry.coordinates)
        .addTo(map);
      pins.push({ marker: marker, element: element, visible: true });
    });

    map.on('load', function () {
      if (destroyed) return;
      mapReady = true;
      if (visitorCoordinates) {
        focusNearby();
      } else if (dealers.length) {
        const bounds = new mapboxgl.LngLatBounds();
        dealers.forEach(function (dealer) { bounds.extend(dealer.geometry.coordinates); });
        map.fitBounds(bounds, { padding: 45, maxZoom: 4.5, duration: 0 });
      }
    });
    map.on('error', function (event) {
      if (event.error) console.warn('PUBLIC Dealer Locator Mapbox error:', event.error);
    });

    searchElement.addEventListener('input', render);
    locateButton.addEventListener('click', useMyLocation);
    if (!navigator.geolocation || !window.isSecureContext) {
      locateButton.disabled = true;
      locationMessage.textContent = 'Location unavailable here. Search by city or ZIP code.';
    }
    render();

    // When auto-location is enabled, request permission once on page load.
    // Browsers control the permission prompt; denial leaves search fully functional.
    if (root.dataset.autoLocate === 'true' && navigator.geolocation && window.isSecureContext) {
      useMyLocation();
    }
  }

  function initAll(scope) {
    if (scope.matches && scope.matches('.public-dealer-locator')) init(scope);
    scope.querySelectorAll('.public-dealer-locator').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    const roots = event.target.matches && event.target.matches('.public-dealer-locator') ?
      [event.target] : Array.from(event.target.querySelectorAll('.public-dealer-locator'));
    roots.forEach(function (root) {
      const instance = instances.get(root);
      if (instance) { instance.destroy(); instances.delete(root); }
    });
  });
})();
